import BaseAPIService from './baseAPI';
import {
  TrendingTopicsRequest,
  TrendingTopicsResponse,
  GeneratePostRequest,
  GeneratePostResponse,
  GeneratePostAsyncResponse,
  GeneratePostTaskStatusResponse,
  GenerationJobListResponse,
  RegeneratePartRequest,
  RegeneratePartResponse,
  ShortenForXRequest,
  ShortenForXResponse,
  ImageSearchRequest,
  ImageSearchResponse,
  ImageSearchSuggestionsRequest,
  ImageSearchSuggestionsResponse,
  ImageSearchBatchRequest,
  ImageSearchBatchResponse,
  ImageSelectRequest,
  ImageCandidate,
} from '../types/api';

const LLM_REQUEST_TIMEOUT_MS = 300_000;
const ASYNC_START_TIMEOUT_MS = 15_000;
const POLL_INTERVAL_MS = 2_500;

class ContentAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  async getTrendingTopics(params: TrendingTopicsRequest): Promise<TrendingTopicsResponse> {
    return this.post<TrendingTopicsResponse>('/content/trending-topics', params);
  }

  async startGeneratePostAsync(
    request: GeneratePostRequest
  ): Promise<GeneratePostAsyncResponse> {
    return this.post<GeneratePostAsyncResponse>(
      '/content/generate-post/async',
      request,
      { timeout: ASYNC_START_TIMEOUT_MS }
    );
  }

  async getGeneratePostTaskStatus(
    taskId: string
  ): Promise<GeneratePostTaskStatusResponse> {
    return this.get<GeneratePostTaskStatusResponse>(
      `/content/generate-post/task/${taskId}`,
      { timeout: 10_000 }
    );
  }

  async getGenerationJobs(params?: {
    activeOnly?: boolean;
    limit?: number;
  }): Promise<GenerationJobListResponse> {
    const search = new URLSearchParams();
    if (params?.activeOnly) search.set('active_only', 'true');
    if (params?.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.get<GenerationJobListResponse>(
      `/content/generation-jobs${qs ? `?${qs}` : ''}`,
      { timeout: 10_000 }
    );
  }

  async pollGeneratePostTask(
    taskId: string,
    onStatus?: (status: string, message?: string) => void
  ): Promise<GeneratePostResponse> {
    const start = Date.now();

    while (Date.now() - start < LLM_REQUEST_TIMEOUT_MS) {
      const task = await this.getGeneratePostTaskStatus(taskId);

      if (onStatus) {
        const message =
          task.status === 'STARTED'
            ? 'Generating your post with AI...'
            : task.status === 'PENDING'
              ? "Your request is queued — we're creating your post..."
              : undefined;
        onStatus(task.status, message);
      }

      if (task.successful && task.result) {
        return task.result;
      }

      if (task.failed) {
        throw new Error(task.error || 'Generation failed');
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    throw new Error(
      'Generation is still in progress. Keep this tab open or try again shortly.'
    );
  }

  /** Queue generation in Celery and poll until complete. */
  async generatePost(
    request: GeneratePostRequest,
    onStatus?: (status: string, message?: string) => void
  ): Promise<GeneratePostResponse> {
    const started = await this.startGeneratePostAsync(request);
    if (onStatus) {
      onStatus('PENDING', started.message);
    }
    return this.pollGeneratePostTask(started.task_id, onStatus);
  }

  async regeneratePart(request: RegeneratePartRequest): Promise<RegeneratePartResponse> {
    return this.post<RegeneratePartResponse>('/content/regenerate-part', request, {
      timeout: LLM_REQUEST_TIMEOUT_MS,
    });
  }

  async shortenForX(request: ShortenForXRequest): Promise<ShortenForXResponse> {
    return this.post<ShortenForXResponse>('/content/shorten-for-x', request, {
      timeout: LLM_REQUEST_TIMEOUT_MS,
    });
  }

  async generateAndSavePost(request: GeneratePostRequest): Promise<GeneratePostResponse> {
    return this.post<GeneratePostResponse>('/content/generate-and-save', request, {
      timeout: LLM_REQUEST_TIMEOUT_MS,
    });
  }

  async searchImage(params: ImageSearchRequest): Promise<ImageSearchResponse> {
    return this.post<ImageSearchResponse>('/content/search-image', params);
  }

  async getImageSearchSuggestions(
    params: ImageSearchSuggestionsRequest
  ): Promise<ImageSearchSuggestionsResponse> {
    return this.post<ImageSearchSuggestionsResponse>('/content/image-search-suggestions', params);
  }

  async searchImagesBatch(params: ImageSearchBatchRequest): Promise<ImageSearchBatchResponse> {
    return this.post<ImageSearchBatchResponse>('/content/search-images', params);
  }

  async selectImage(params: ImageSelectRequest): Promise<ImageSearchResponse> {
    return this.post<ImageSearchResponse>('/content/select-image', params);
  }

  async getImageSuggestions(topic: string): Promise<{ data: string[] }> {
    return this.getImageSearchSuggestions({ topic, content: topic, count: 6 });
  }

  async optimizeContent(content: string, platform: string): Promise<{ data: { content: string } }> {
    return this.post<{ data: { content: string } }>('/content/optimize', { content, platform });
  }

  async generateHashtags(content: string): Promise<{ data: string[] }> {
    return this.post<{ data: string[] }>('/content/generate-hashtags', { content });
  }

  async getTrendingTopicsByDomain(domain: string = 'all', maxResults: number = 5): Promise<TrendingTopicsResponse> {
    return this.post<TrendingTopicsResponse>(`/content/trending-topics-by-domain?domain=${domain}&max_results=${maxResults}`, {});
  }

  async getTrendingDomains(): Promise<{ domains: Array<{ value: string; label: string }> }> {
    return this.get<{ domains: Array<{ value: string; label: string }> }>('/content/trending-domains');
  }

  async searchTrendingTopics(query: string, maxResults: number = 10): Promise<TrendingTopicsResponse> {
    return this.post<TrendingTopicsResponse>(`/content/search-trending-topics?query=${encodeURIComponent(query)}&max_results=${maxResults}`, {});
  }
}

export const contentAPI = new ContentAPIService();
export default contentAPI;
